/**
 * AnswersLeftChip — header pill for free users showing remaining free
 * chart-grounded answers (CHAT_FREE_CAP + referral bonuses).
 *
 * Renders NOTHING for paid users (BASIC+) or while the initial fetch
 * is inflight — no layout shift on the header.
 *
 * Auto-refreshes on:
 *   • mount
 *   • the `lodestar:chat_usage_changed` window event
 *     (fired by PersonalizedAnswerFlow after each successful answer
 *     stream and by the referral toast after a friend joins).
 *   • window focus (catches usage burned in another tab).
 *
 * Visual states:
 *   • remaining >= 4  → muted white pill, "N free answers left"
 *   • remaining 1..3  → gold pill, subtle pulse, "N left · refer for +10"
 *   • remaining == 0  → gold pill, "Free cap reached — upgrade"
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { CHAT_USAGE_EVENT } from "@/lib/anonKey";
import { API } from "@/lib/api";

export default function AnswersLeftChip({ variant = "dark", compact = false }) {
    const [state, setState] = useState(null);   // null = pre-fetch (hidden)
    const [errored, setErrored] = useState(false);

    const refresh = useCallback(async () => {
        try {
            // SEC-001 hardening: the cap identity is server-derived
            // (auth cookie → trusted-hop IP hash). We no longer send
            // the client anon_key — it can't change what the endpoint
            // returns and previously enabled a P3 count-enumeration.
            const r = await fetch(`${API}/ask/usage`, { credentials: "include" });
            if (!r.ok) throw new Error(`HTTP ${r.status}`);
            const j = await r.json();
            setState(j);
            setErrored(false);
        } catch {
            setErrored(true);
        }
    }, []);

    useEffect(() => {
        refresh();
        const onChange = () => refresh();
        const onFocus  = () => refresh();
        window.addEventListener(CHAT_USAGE_EVENT, onChange);
        window.addEventListener("focus", onFocus);
        return () => {
            window.removeEventListener(CHAT_USAGE_EVENT, onChange);
            window.removeEventListener("focus", onFocus);
        };
    }, [refresh]);

    if (errored || !state) return null;
    if (state.unlimited || state.is_paid) return null;

    const remaining = state.remaining;
    const cap = state.cap;
    const low = remaining <= 3 && remaining > 0;
    const exhausted = remaining <= 0;

    const dark = variant !== "light";
    const base = compact
        ? "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-medium transition-colors"
        : "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors";

    let toneCls;
    if (exhausted) {
        toneCls = dark
            ? "bg-lodestar-gold-royal/15 border border-lodestar-gold-bright/60 text-lodestar-gold-bright hover:bg-lodestar-gold-royal/25"
            : "bg-amber-100 border border-amber-500/60 text-amber-800 hover:bg-amber-200";
    } else if (low) {
        toneCls = dark
            ? "bg-lodestar-gold-royal/10 border border-lodestar-gold-bright/40 text-lodestar-gold-bright hover:bg-lodestar-gold-royal/20"
            : "bg-amber-50 border border-amber-400/50 text-amber-700 hover:bg-amber-100";
    } else {
        toneCls = dark
            ? "bg-white/[0.04] border border-white/10 text-white/75 hover:text-white hover:border-white/25"
            : "bg-stone-100 border border-stone-200 text-stone-700 hover:bg-stone-200";
    }

    const bonusLabel = state.bonus > 0 ? ` (incl. +${state.bonus} referral)` : "";
    const title = exhausted
        ? `You've used all ${cap} free chart-grounded answers${bonusLabel}. Upgrade for unlimited.`
        : `${remaining} of ${cap} free chart-grounded answers remaining${bonusLabel}. Refer a friend to earn +10.`;

    const label = exhausted
        ? "Free cap reached — upgrade"
        : compact
            ? `${remaining}/${cap} left`
            : `${remaining} free answer${remaining === 1 ? "" : "s"} left`;

    // Link target: exhausted → /pricing, low → /account?tab=referrals for the
    // referral share (turns scarcity into a viral loop), otherwise pricing.
    const href = exhausted || !low ? "/pricing" : "/account?tab=referrals";

    return (
        <Link
            to={href}
            data-testid="answers-left-chip"
            data-remaining={remaining}
            data-cap={cap}
            title={title}
            aria-label={title}
            className={`${base} ${toneCls} ${low ? "animate-pulse" : ""}`}
        >
            <Sparkles className={compact ? "w-3 h-3" : "w-3.5 h-3.5"} strokeWidth={1.75} />
            <span>{label}</span>
        </Link>
    );
}
